/**
 * Компонент страницы ошибки оплаты.
 *
 * Отображается после перенаправления клиента с IntellectMoney
 * при неудачной оплате или повторном открытии уже отменённого платежа.
 */

import { useEffect, useMemo, useState } from 'react';
import { reachOrderFlowGoal } from '../utils/metrika';
import { buildPaymentResultApiUrl } from '../utils/apiBase';

function getFailureInfo(payment) {
  const status = payment?.status;
  const explicitError = payment?.error_message?.trim();

  if (explicitError) {
    return {
      title: 'Платёж завершился с ошибкой',
      description: explicitError,
      note: 'Средства не были списаны или будут автоматически возвращены банком.',
    };
  }

  if (status === 'cancelled') {
    return {
      title: 'Счёт на оплату был отменён',
      description: 'Срок действия ссылки истёк или платёж был аннулирован до завершения оплаты.',
      note: 'Чтобы завершить покупку, создайте новый платёж и оплатите заказ ещё раз.',
    };
  }

  if (status === 'refunded') {
    return {
      title: 'Платёж был отменён и возвращён',
      description: 'Оплата не завершилась, а деньги по этой операции уже возвращены.',
      note: 'Вы можете создать новый платёж и повторить попытку.',
    };
  }

  if (status === 'pending') {
    return {
      title: 'Оплата не была завершена',
      description: 'Платёжная система не подтвердила оплату по этой ссылке.',
      note: 'Чаще всего это происходит, если страница была закрыта или подтверждение оплаты не было завершено.',
    };
  }

  return {
    title: 'Оплата не прошла',
    description: 'Платёжная система не смогла завершить оплату по этой ссылке.',
    note: 'Попробуйте создать новый платёж и повторить попытку.',
  };
}

function closePage() {
  if (window.parent !== window) {
    try {
      window.parent.postMessage({ type: 'closePreview' }, '*');
      return;
    } catch (_) {
      // Игнорируем ошибки postMessage и пробуем следующие варианты закрытия.
    }
  }

  if (window.vkBridge?.send) {
    window.vkBridge.send('VKWebAppClose').catch(() => {
      window.close();
    });
    return;
  }

  window.close();
}

export function PaymentFail({ paymentId }) {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(Boolean(paymentId));
  const [loadError, setLoadError] = useState('');
  const [retryLoading, setRetryLoading] = useState(false);
  const [retryError, setRetryError] = useState('');

  useEffect(() => {
    if (!paymentId) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadOrder = async () => {
      try {
        const response = await fetch(buildPaymentResultApiUrl(`/orders/api/payments/${paymentId}/order/`));
        if (!response.ok) {
          throw new Error('Не удалось получить статус платежа');
        }

        const data = await response.json();
        if (cancelled) {
          return;
        }

        setOrderData(data);
        setLoadError('');
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error('Error loading failed payment info:', error);
        setLoadError(error.message || 'Не удалось получить детали платежа');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrder();

    return () => {
      cancelled = true;
    };
  }, [paymentId]);

  const failureInfo = useMemo(
    () => getFailureInfo(orderData?.payment),
    [orderData],
  );

  useEffect(() => {
    if (!paymentId || !orderData?.order_type) {
      return;
    }

    reachOrderFlowGoal(orderData.order_type, 'payment_fail', {
      payment_id: paymentId,
      order_id: orderData.id,
      order_type: orderData.order_type,
      payment_status: orderData?.payment?.status,
      traffic_source: orderData.traffic_source || '',
      universal_app: orderData.universal_app || '',
    });
  }, [paymentId, orderData?.id, orderData?.order_type, orderData?.payment?.status]);

  const handleRetryPayment = async () => {
    if (!orderData?.id || retryLoading) {
      return;
    }

    setRetryLoading(true);
    setRetryError('');

    try {
      const response = await fetch(buildPaymentResultApiUrl(`/orders/api/${orderData.id}/payment/`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payment_method: 'online' }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.error || 'Не удалось создать новый платёж');
      }

      if (!data?.payment_url) {
        throw new Error('Платёжная ссылка не вернулась от сервера');
      }

      window.location.href = data.payment_url;
    } catch (error) {
      console.error('Retry payment failed:', error);
      setRetryError(error.message || 'Не удалось создать новый платёж');
      setRetryLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden max-w-xl mx-auto">
      <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white text-center py-8 px-6">
        <div className="text-7xl mb-3">
          <span role="img" aria-label="Ошибка">❌</span>
        </div>
        <h1 className="text-2xl font-bold">{failureInfo.title}</h1>
      </div>

      <div className="p-6 space-y-5">
        {paymentId && (
          <div className="text-center bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">ID платежа</span>
            <p className="text-lg font-bold text-gray-900 dark:text-white">{paymentId}</p>
          </div>
        )}

        {loading ? (
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red-500 mx-auto mb-3"></div>
            <p className="text-sm text-gray-600 dark:text-gray-300">Уточняем причину ошибки...</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <span className="text-lg flex-shrink-0">⚠️</span>
                <div className="space-y-1">
                  <p className="text-sm font-semibold text-red-900 dark:text-red-200">
                    {failureInfo.description}
                  </p>
                  <p className="text-sm text-red-800 dark:text-red-300">
                    {failureInfo.note}
                  </p>
                </div>
              </div>
            </div>

            {orderData?.payment?.status_display && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                  Статус платежа
                </div>
                <div className="text-sm font-semibold text-gray-900 dark:text-white">
                  {orderData.payment.status_display}
                </div>
              </div>
            )}

            {loadError && (
              <div className="rounded-xl border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-4 text-sm text-yellow-900 dark:text-yellow-200">
                {loadError}
              </div>
            )}

            {retryError && (
              <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-900 dark:text-red-200">
                {retryError}
              </div>
            )}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={handleRetryPayment}
            disabled={!orderData?.id || retryLoading}
            className="flex-1 py-3 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:cursor-not-allowed cursor-pointer"
          >
            {retryLoading ? 'Создаём новую ссылку...' : 'Оплатить снова'}
          </button>
          <button
            type="button"
            onClick={closePage}
            className="flex-1 py-3 px-6 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 cursor-pointer"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
